import { useState, useEffect, useMemo } from "react";
import { Eye, FileDown, Flame } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import ScoutingReportManager from "../../managers/ScoutingReportsManager";
import { fetchAPI, fetchAPIBlob } from "../../config/api";
import { toDivisionApi } from "../../config/constants";
import { ReportHeader } from "../../components/reports";
import { WindowSection } from "./components";

const WINDOWS = [25, 50, 100];
const WINDOW_CUTOFFS = { 25: 0.100, 50: 0.075, 100: 0.050 };

export const PlayersToWatchPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowData, setWindowData] = useState({});
  const [headshotMap, setHeadshotMap] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const reportData = await ScoutingReportManager.getReportById(reportId);
        setReport(reportData);
        if (!reportData) return;

        const division = toDivisionApi(reportData.division);
        const year = reportData.year;

        const responses = await Promise.all(
          WINDOWS.flatMap((w) => [
            fetchAPI(`/leaderboards/rolling?division=${division}&years=${year}&window=${w}&player_type=batter`),
            fetchAPI(`/leaderboards/rolling?division=${division}&years=${year}&window=${w}&player_type=pitcher`),
          ])
        );

        const combined = {};
        WINDOWS.forEach((w, i) => {
          const raw = (r) => (Array.isArray(r) ? r : r?.items || []);
          combined[w] = {
            batters: raw(responses[i * 2]),
            pitchers: raw(responses[i * 2 + 1]),
          };
        });
        setWindowData(combined);

        const { teamId, teamName } = reportData;
        const teamPlayerIds = new Set();
        Object.values(combined).forEach(({ batters, pitchers }) => {
          [...batters, ...pitchers].forEach((p) => {
            if (p.team_id === teamId || p.team_name === teamName) {
              teamPlayerIds.add(p.player_id);
            }
          });
        });

        if (teamPlayerIds.size > 0) {
          const bulk = await fetchAPI("/players/bulk", {
            method: "POST",
            body: JSON.stringify({ player_ids: [...teamPlayerIds] }),
          });
          const map = {};
          Object.entries(bulk || {}).forEach(([pid, info]) => {
            if (info?.headshot_url) map[pid] = info.headshot_url;
          });
          setHeadshotMap(map);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (reportId) load();
  }, [reportId]);

  const sections = useMemo(() => {
    if (!report || Object.keys(windowData).length === 0) return [];
    const { teamId, teamName } = report;
    const isOnTeam = (p) => p.team_id === teamId || p.team_name === teamName;

    return WINDOWS.map((w) => {
      const cutoff = WINDOW_CUTOFFS[w];
      const isNotable = (p) => Math.abs(Number(p.woba_change)) >= cutoff;
      const { batters = [], pitchers = [] } = windowData[w] || {};
      return {
        window: w,
        batters: batters
          .filter((p) => isOnTeam(p) && isNotable(p))
          .sort((a, b) => Number(b.woba_change) - Number(a.woba_change)),
        pitchers: pitchers
          .filter((p) => isOnTeam(p) && isNotable(p))
          .sort((a, b) => Number(a.woba_change) - Number(b.woba_change)),
      };
    });
  }, [report, windowData]);

  const hasAnyPlayers = sections.some((s) => s.batters.length > 0 || s.pitchers.length > 0);

  const exportPayload = () => ({
    sections,
    headshotMap,
    report: { teamName: report.teamName, year: report.year, teamId: report.teamId },
  });

  const handlePreview = () => {
    sessionStorage.setItem("playersToWatchConfig", JSON.stringify(exportPayload()));
    window.open("/render/players-to-watch", "_blank");
  };

  const handleExport = async () => {
    if (exporting || !report) return;
    setExporting(true);
    const loadingToast = toast.loading("Generating PDF...");
    try {
      const blob = await fetchAPIBlob("/reports/players_to_watch", {
        method: "POST",
        body: JSON.stringify(exportPayload()),
      });
      saveAs(blob, `${report.teamId || "team"}_players_to_watch_${report.year}.pdf`);
      toast.success("PDF downloaded!", { id: loadingToast });
    } catch (err) {
      console.error(err);
      toast.error("Failed to export PDF", { id: loadingToast });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Report Not Found</h2>
          <button
            onClick={() => navigate("/scouting")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Scouting
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <ReportHeader
          teamId={report.teamId}
          teamName={report.teamName}
          reportType="Players to Watch"
          year={report.year}
        >
          <button
            onClick={handlePreview}
            disabled={!hasAnyPlayers}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Eye size={14} />
            Preview
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || !hasAnyPlayers}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileDown size={14} />
            {exporting ? "Exporting…" : "Export PDF"}
          </button>
        </ReportHeader>

        {!hasAnyPlayers ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <Flame size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              No rolling leaderboard data found for {report.teamName}.
            </p>
            <p className="text-gray-400 text-sm mt-1">
              This may mean no players have enough plate appearances in these windows yet.
            </p>
          </div>
        ) : (
          sections.map((s) => (
            <WindowSection
              key={s.window}
              window={s.window}
              batters={s.batters}
              pitchers={s.pitchers}
              headshotMap={headshotMap}
              cutoff={WINDOW_CUTOFFS[s.window]}
            />
          ))
        )}
      </div>
    </div>
  );
};
